import { useMemo } from 'react';
import { Glyphicon, Label, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';
import { ITransplantedRecipient } from '@nkr/models/index';
import { PatientType } from '@nkr/enums/index';
import { AgGridReact } from 'ag-grid-react';
import { ValueGetterParams, ValueFormatterParams, GridReadyEvent, FirstDataRenderedEvent, IHeaderParams, ColDef, ICellRendererParams, ColSpanParams, GridSizeChangedEvent, SortChangedEvent } from 'ag-grid-community';
import { GridWrapper } from './styles';
import { ActionButtonRenderer } from './action-button-renderer';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import AlleleMatch from '@nkr/components/allele-match';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';
import { useMediaQuery } from 'react-responsive';

interface IProps {
    list: ITransplantedRecipient[]
    centerId: number,
    isSuperUser: boolean,
    container: any
}

const TransplantedRecipientList = (props: IProps) => {
    const isBigScreen = useMediaQuery({ minWidth: 1824 });

    const onGridReady = (e: GridReadyEvent) => {
        // gridApi = e.api;
        // columnApi = e.columnApi;
    };

    const onDataFirstRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        var grid = document.getElementById('grid-wrapper');

        if (grid == null)
            return;

        var gridWidth = grid.offsetWidth;

        // keep track of which columns to hide/show
        var columnsToShow = [];
        var columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        var totalColsWidth = 0;
        var allColumns = e.columnApi.getAllColumns();

        for (var i = 0; i < allColumns.length; i++) {
            var column = allColumns[i];

            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const columns: ColDef[] = [
        {
            headerName: 'R#',
            field: 'rank',
            width: 45,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            headerName: 'Type',
            field: 'patientTypeId',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedRecipient;
                let style = 'info';

                if (data.patientTypeId == PatientType.KidneyReplacementCase) {
                    style = 'danger';
                }

                if (data.patientTypeId == PatientType.Paired) {
                    style = 'default';
                }

                if (data.patientTypeId == PatientType.Waitlist) {
                    style = 'warning';
                }

                if (data.patientTypeId == PatientType.StandardVoucherHolder || data.patientTypeId == PatientType.FamilyVoucherHolder) {
                    style = 'success';
                }

                return <Label bsStyle={style}>{data.patientTypeAbbr}</Label>;
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITransplantedRecipient;
                return data.patientType;
            }
        },
        {
            headerName: 'Registered',
            field: 'registeredDate',
            width: 110,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registered</SortableHeaderComponent>,
            comparator: Utility.dateComparator,
        },
        {
            field: 'alias',
            headerName: 'Alias',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>Alias</SortableHeaderComponent>,
            width: 120
        },
        {
            headerName: 'Name',
            width: 200,
            hide: !isBigScreen,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.data.firstName + ' ' + params.data.lastName
        },
        {
            headerName: 'Email',
            field: 'email',
            width: 180,
            hide: !isBigScreen,
            cellStyle: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Email</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return <span title={params.data.email}>{params.data.email}</span>;
            }
        },
        {
            field: 'gender',
            headerName: 'Gender',
            headerComponentFramework: (props: IHeaderParams) =>
                <SortableHeaderComponent {...props}>G</SortableHeaderComponent>,
            width: 60
        },
        {
            headerName: 'Age',
            field: 'age',
            width: 55,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
        },
        {
            field: 'bloodType',
            headerName: 'Rec<br />ABO',
            width: 50,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>ABO</SortableHeaderComponent>,
        },
        {
            headerName: 'Int. Donor ABO',
            field: 'donorBloodTypes',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Intended<br />Donor<br />ABO</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ITransplantedRecipient;

                return data.patientTypeId == PatientType.Waitlist && !data.donorBloodTypes ? 'N/A' : data.donorBloodTypes;
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let value = params.value as string;

                if (value.split(',').length > 1) {
                    return (
                        <span style={{ display: 'block', width: 80, wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: '15px' }}>{value}</span>
                    )
                }

                return (
                    <>{value}</>
                );
            }
        },

        {
            headerName: 'Actual Donor ABO',
            field: 'actualDonorBloodType',
            width: 85,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actual<br />Donor<br />ABO</SortableHeaderComponent>,
        },
        {
            headerName: 'Actual Donor Age',
            field: 'actualDonorAge',
            width: 85,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actual<br />Donor<br />Age</SortableHeaderComponent>,
        },

        {
            headerName: 'High Res Typing',
            field: 'has3Typing',
            width: 85,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>High<br />Res<br />Typing</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedRecipient;
                return (
                    <div style={{ paddingTop: 3 }}>{data.has3Typing ? <Glyphicon glyph='ok-sign' /> : null}</div>
                );
            },
        },
        {
            field: 'avoids',
            headerName: 'Avoids',
            width: 75,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Avoids</SortableHeaderComponent>,
        },
        {
            field: 'cpra4',
            headerName: 'NKR<br />cPRA4',
            width: 80,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NKR<br />cPRA4</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as ITransplantedRecipient;
                const cpra4 = data.cpra4.toFixed(2);
                return cpra4 + '%';
            }
        },
        {
            headerName: 'Actual Wait',
            field: 'actualWaitTime',
            width: 75,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actual<br />Wait<div style={{ fontSize: 12 }}>(months)</div></SortableHeaderComponent>,
        },
        {
            headerName: 'Voucher Wait',
            field: 'voucherWaitTime',
            width: 75,
            valueFormatter: (params: ValueFormatterParams) => (!params.value ? 'N/A' : params.value),
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Voucher<br />Wait<div style={{ fontSize: 12 }}>(months)</div></SortableHeaderComponent>,
        },
        {
            headerName: 'Microsite',
            field: 'micrositeRecipientId',
            width: 95,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Microsite</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedRecipient;

                return (
                    <div style={{ paddingTop: 3 }}>{data.micrositeRecipientId > 0 ? <Glyphicon glyph='ok-sign' /> : null}</div>
                );
            },
        },
        {
            headerName: 'Eplet MM',
            field: 'singleEpletMmRisk.sortValue',
            width: 75,
            cellClass: 'list-cell-full',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Eplet<br />MM</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedRecipient;

                return (
                    <SingleEpletMmRisk data={data.singleEpletMmRisk} centerId={props.centerId} isAdmin={false} placement='bottom' />
                )
            },

        },
        {
            headerName: 'Allele Match',
            field: 'bmtMm',
            width: 75,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Allele<br />MM</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <AlleleMatch bmtMm={params.value} displayAsInline={false} />,
        },
        {
            field: 'transplantDate',
            headerName: 'Transplanted',
            width: 110,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP Date</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            field: 'actions',
            headerName: '',
            width: 100,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Actions</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) =>
                <div style={{ width: '100%', textAlign: 'center' }}>
                    <ActionButtonRenderer {...params}
                        pullRight={true}
                        isAdmin={props.isSuperUser}
                        data={params.data}
                        onMenuToggle={null}
                        onItemSelected={null} />
                </div>
        }
    ];

    const columnDefs = useMemo(() => columns, [isBigScreen]);
    const rowData = useMemo(() => props.list, [props.list]);

    return (
        <>
            <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap'>
                <AgGridReact
                    defaultColDef={{
                        sortable: true
                    }}
                    columnDefs={columnDefs}
                    rowData={rowData}
                    headerHeight={62}
                    rowHeight={36}
                    suppressCellSelection={true}
                    suppressContextMenu={true}
                    enableCellTextSelection={true}
                    rowStyle={{ overflow: 'visible', cursor: 'pointer' }}
                    noRowsOverlayComponentFramework={() => <div>No results found...</div>}
                    onGridReady={onGridReady}
                    onGridSizeChanged={onGridSizeChanged}
                    onFirstDataRendered={onDataFirstRendered}
                    onSortChanged={onSortChanged}
                />
            </GridWrapper>

            {/* <div style={{ marginBottom: 5 }}>{props.list.length} of {props.list.length} records shown...</div> */}
        </>
    );
}

export default TransplantedRecipientList;